import { Filter, filterBySelect } from '@innovyze/lib-am-common';
import { COVERAGE_THRESHOLDS } from '@Components/RiskDetails/Datagrid/RenderCell/coverageBand';

// Maps MUI grid filter items to the risk API's filter contract
// (I360A-2729a): COLKEY|value|op with op ∈ eq/gte/lt only. One entry per
// column carries its precomputed filters, because the lib filter store keys
// by column and would otherwise drop the first bound of a range.

export const ATTRIBUTE_FILTER_FIELDS = [
	'MATERIAL',
	'DIAMETER',
	'INSTALLATION_YEAR',
	'CCTV_INSPECTED',
	'DATA_COVERAGE',
];

export interface GridFilterItemLike {
	field: string;
	operator: string;
	value?: unknown;
}

export interface GridFilterEntry {
	colKey: string;
	value: string | string[];
	filters?: Filter[];
}

type Op = 'gte' | 'lt';
const ranged = (colKey: string, value: number, op: Op): Filter => ({
	colKey,
	value: String(value),
	operator: op as Filter['operator'],
});

const isBlank = (v: unknown) =>
	v === null || v === undefined || (typeof v === 'string' && v.trim() === '');

const yearFilters = (
	colKey: string,
	operator: string,
	value: unknown,
): Filter[] => {
	if (isBlank(value)) return [];
	const n = Number(value);
	if (!Number.isInteger(n)) return [];
	switch (operator) {
		case '=':
			return [
				{
					colKey,
					value: String(n),
					operator: 'eq' as Filter['operator'],
				},
			];
		case '>=':
			return [ranged(colKey, n, 'gte')];
		case '<':
			return [ranged(colKey, n, 'lt')];
		case '>':
			return [ranged(colKey, n + 1, 'gte')];
		case '<=':
			return [ranged(colKey, n + 1, 'lt')];
		default:
			return [];
	}
};

const diameterFilters = (
	colKey: string,
	operator: string,
	value: unknown,
): Filter[] => {
	if (operator !== '>=' && operator !== '<') return [];
	if (isBlank(value)) return [];
	// The value was already converted to the stored unit (convertFilterUnits).
	const n = Number(value);
	if (!Number.isFinite(n)) return [];
	return [ranged(colKey, n, operator === '>=' ? 'gte' : 'lt')];
};

const materialFilters = (
	colKey: string,
	operator: string,
	value: unknown,
): Filter[] => {
	if (operator !== 'equals' || typeof value !== 'string') return [];
	const trimmed = value.trim();
	// The backend runs an extra decodeURIComponent on values ('%' throws a
	// URIError -> 500) and splits on '|' and ',' ('|' breaks the parse, ',' becomes
	// two IN values), so such values cannot round-trip: send no filter.
	if (!trimmed || /[%|,]/.test(trimmed)) return [];
	return [{ colKey, value: trimmed }];
};

// Backend CCTV_INSPECTED is a text column bound as 'true'/'false' (equality only).
const cctvFilters = (
	colKey: string,
	operator: string,
	value: unknown,
): Filter[] => {
	if (operator !== 'is' || (value !== 'true' && value !== 'false')) return [];
	return [{ colKey, value }];
};

// Bands use the same thresholds as the coverage label (coverageBand.ts).
const coverageFilters = (
	colKey: string,
	operator: string,
	value: unknown,
): Filter[] => {
	if (operator !== 'is') return [];
	switch (value) {
		case 'complete':
			return [ranged(colKey, COVERAGE_THRESHOLDS.complete, 'gte')];
		case 'partial':
			return [
				ranged(colKey, COVERAGE_THRESHOLDS.partial, 'gte'),
				ranged(colKey, COVERAGE_THRESHOLDS.complete, 'lt'),
			];
		case 'minimal':
			return [ranged(colKey, COVERAGE_THRESHOLDS.partial, 'lt')];
		default:
			return [];
	}
};

const buildFilters = (item: GridFilterItemLike): Filter[] => {
	switch (item.field) {
		case 'INSTALLATION_YEAR':
			return yearFilters(item.field, item.operator, item.value);
		case 'DIAMETER':
			return diameterFilters(item.field, item.operator, item.value);
		case 'MATERIAL':
			return materialFilters(item.field, item.operator, item.value);
		case 'CCTV_INSPECTED':
			return cctvFilters(item.field, item.operator, item.value);
		case 'DATA_COVERAGE':
			return coverageFilters(item.field, item.operator, item.value);
		default:
			return [];
	}
};

export function mapGridFilterItems(
	items: GridFilterItemLike[],
): GridFilterEntry[] {
	// Legacy fields (GRADE, CONDITION_DATA): exactly today's mapping.
	const legacy: GridFilterEntry[] = items
		.filter(item => !ATTRIBUTE_FILTER_FIELDS.includes(item.field))
		.map(item => ({
			colKey: item.field,
			value: item.value as string | string[],
		}))
		.filter(entry => entry.value && entry.colKey);

	const grouped: Record<string, Filter[]> = {};
	const order: string[] = [];
	items.forEach(item => {
		if (!ATTRIBUTE_FILTER_FIELDS.includes(item.field)) return;
		const filters = buildFilters(item);
		if (!filters.length) return;
		if (!grouped[item.field]) {
			grouped[item.field] = [];
			order.push(item.field);
		}
		grouped[item.field] = grouped[item.field].concat(filters);
	});
	const attributes: GridFilterEntry[] = order.map(colKey => ({
		colKey,
		value: grouped[colKey].map(f => f.value as string).join(','),
		filters: grouped[colKey],
	}));

	return legacy.concat(attributes);
}

// filterBy for the attribute selects: the filters were already built from the
// grid item (operator mapping), so pass them straight through.
export const filterByPrecomputed: filterBySelect = option =>
	(option as GridFilterEntry).filters ?? [];
